import { useCallback, useMemo } from 'react';

import { electronApis } from '@/features/electron-api';

import { handleLocalTerminalKey } from './local-terminal-keys';
import { sandboxTerminalPanelStore } from './sandbox-terminal-panel.store';
import {
  buildLocalTerminalWsUrl,
  describeLocalTerminalClose,
  encodeLocalTerminalAuthMessage,
  type AuthAck,
} from './sandbox-terminal-protocol';
import { TerminalView, type TerminalConnection } from './TerminalView';

interface LocalTerminalProps {
  /** Tab this terminal backs; also the React key its panel mounts it under. */
  tabKey: string;
  header: React.ReactNode;
}

/**
 * One local terminal tab: a shell on this machine, hosted by the bundled bun PTY
 * host (pty-host/pty-host.ts) that Electron main spawns on first use. Reattaches
 * to the tab's host session when it has one, so a remount (reload) gets the same
 * shell with its scrollback replayed. An interactive tab is removed from the store
 * when the shell ends; a run tab (agent command, `tab.command` set) stays with its
 * output and an exit banner until the user closes it. Electron-only; gated by the
 * `local-terminal` flag at the panel.
 */
export function LocalTerminal({ tabKey, header }: LocalTerminalProps) {
  // `command` is fixed at tab creation, so reading it once per mount is enough.
  const isRunTab =
    typeof sandboxTerminalPanelStore.tabs.find((t) => t.key === tabKey)
      ?.command === 'string';
  const resolveConnection = useCallback(
    async ({
      cols,
      rows,
    }: {
      cols: number;
      rows: number;
    }): Promise<TerminalConnection> => {
      const endpoint = await electronApis.localTerminal.getEndpoint();
      if (!endpoint) throw new Error('Local terminal host unavailable');
      const tab = sandboxTerminalPanelStore.tabs.find((t) => t.key === tabKey);
      return {
        wsUrl: buildLocalTerminalWsUrl(endpoint.port, cols, rows),
        authFrame: encodeLocalTerminalAuthMessage(
          endpoint.token,
          cols,
          rows,
          tab?.sessionId ?? undefined,
          // a run tab shows one command; never let it become a fresh shell
          { attachOnly: isRunTab }
        ),
        // the host spawns (or resizes on attach) at this grid and acks it
        authFrameCarriesGrid: true,
      };
    },
    [tabKey, isRunTab]
  );

  // Per-tab callbacks, stable for the tab's life (TerminalView effect deps).
  const callbacks = useMemo(
    () => ({
      onAuthed: (ack: AuthAck) => {
        if (ack.session) {
          sandboxTerminalPanelStore.setTabSession(tabKey, ack.session);
        }
      },
      onTitle: (title: string) =>
        sandboxTerminalPanelStore.setTabTitle(tabKey, title),
      onClose: () => {
        const tab = sandboxTerminalPanelStore.tabs.find(
          (t) => t.key === tabKey
        );
        if (typeof tab?.command === 'string') {
          sandboxTerminalPanelStore.markExited(tabKey);
        } else {
          sandboxTerminalPanelStore.removeTab(tabKey);
        }
      },
      registerFocus: (focus: () => void) =>
        sandboxTerminalPanelStore.registerLocalFocus(tabKey, focus),
      registerKill: (kill: () => boolean) =>
        sandboxTerminalPanelStore.registerLocalKill(tabKey, kill),
    }),
    [tabKey]
  );

  return (
    <TerminalView
      title="Terminal"
      header={header}
      onDockClose={() => sandboxTerminalPanelStore.hideLocal()}
      resolveConnection={resolveConnection}
      describeClose={describeLocalTerminalClose}
      onKeyCommand={handleLocalTerminalKey}
      onAuthed={callbacks.onAuthed}
      onTitle={callbacks.onTitle}
      onClose={callbacks.onClose}
      exitBanner={isRunTab}
      killOnClose
      registerFocus={callbacks.registerFocus}
      registerKill={callbacks.registerKill}
    />
  );
}
